import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
import ProgressBar from '@/components/ProgressBar';

interface ProspectGoal {
  id: string;
  title: string;
  raised_cents: number;
  target_cents: number;
}

interface Prospect {
  id: string;
  name: string;
  headline?: string;
  avatar_url?: string;
  cover_url?: string;
  sport?: string;
  position?: string;
  city?: string;
  state?: string;
  goals?: ProspectGoal[];
}

function formatLocation(prospect: Prospect) {
  if (prospect.city && prospect.state) return `${prospect.city}, ${prospect.state}`;
  return prospect.city || prospect.state || '';
}

function ProspectCard({ prospect }: { prospect: Prospect }) {
  const primaryGoal = prospect.goals?.[0] || null;
  const location = formatLocation(prospect);

  return (
    <Link
      to={`/prospects/${prospect.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl bg-white/80 shadow-soft transition hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="relative h-40 w-full overflow-hidden bg-sand/60">
        {prospect.cover_url ? (
          <img src={prospect.cover_url} alt="Prospect hero" className="h-full w-full object-cover transition group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-cocoa/50">Team spirit</div>
        )}
        {prospect.avatar_url ? (
          <img
            src={prospect.avatar_url}
            alt={prospect.name}
            className="absolute bottom-3 left-3 h-16 w-16 rounded-full border-4 border-white object-cover shadow-md"
          />
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <h3 className="text-lg font-semibold text-cocoa">{prospect.name}</h3>
          {prospect.headline ? <p className="text-sm text-cocoa/70">{prospect.headline}</p> : null}
          <p className="mt-1 text-xs uppercase tracking-wide text-cocoa/60">
            {prospect.sport}
            {prospect.position ? ` • ${prospect.position}` : ''}
            {location ? ` • ${location}` : ''}
          </p>
        </div>
        {primaryGoal ? (
          <ProgressBar
            value={primaryGoal.raised_cents}
            max={primaryGoal.target_cents}
            label={<span className="text-xs font-medium text-cocoa/70">{primaryGoal.title}</span>}
          />
        ) : (
          <p className="text-sm text-cocoa/60">Help {prospect.name} reach their next milestone.</p>
        )}
        <span className="text-sm font-semibold text-brick group-hover:underline">Sponsor this athlete →</span>
      </div>
    </Link>
  );
}

export default function ProspectsList() {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sportFilter, setSportFilter] = useState('all');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${CORE_API}/api/v1/prospects/public`);
        if (!res.ok) throw new Error('Failed to load prospects');
        const data = await res.json();
        if (!active) return;
        setProspects(data.items || []);
        setError(null);
      } catch (err: unknown) {
        if (!active) return;
        setError(err instanceof Error ? err.message : 'Failed to load prospects');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const sports = useMemo(() => {
    const unique = new Set(prospects.map((p) => p.sport).filter(Boolean));
    return ['all', ...Array.from(unique).sort()];
  }, [prospects]);

  const filteredProspects = useMemo(() => {
    if (sportFilter === 'all') return prospects;
    return prospects.filter((prospect) => prospect.sport === sportFilter);
  }, [prospects, sportFilter]);

  return (
    <section className="space-y-8">
      <header className="mx-auto max-w-3xl text-center">
        <h1 className="text-3xl font-semibold text-cocoa">Sponsor a Prospect</h1>
        <p className="mt-3 text-cocoa/70">
          Meet young athletes pursuing big dreams. Your sponsorship helps cover travel, competition fees, coaching, and the
          support systems they need to thrive.
        </p>
      </header>

      <div className="flex flex-wrap items-center justify-center gap-2">
        {sports.map((sport) => (
          <button
            key={sport}
            type="button"
            onClick={() => setSportFilter(sport ?? 'all')}
            className={`rounded-full border px-4 py-1 text-sm transition ${
              sportFilter === sport ? 'border-brick bg-brick text-white' : 'border-sand bg-white text-cocoa/70 hover:bg-shell'
            }`}
          >
            {sport === 'all' ? 'All sports' : sport}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rounded-2xl border border-sand/60 bg-white/70 p-6 text-center text-cocoa/70">Loading prospects…</div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center text-rose-600">{error}</div>
      ) : filteredProspects.length ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProspects.map((prospect) => (
            <ProspectCard key={prospect.id} prospect={prospect} />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-sand/60 bg-white/70 p-6 text-center text-cocoa/70">
          No prospects available for this sport yet. Check back soon!
        </div>
      )}
    </section>
  );
}
